import { NavLink, Outlet } from 'react-router-dom';
import {
  Palette, SlidersHorizontal, Building2, Building, CalendarDays, AlertTriangle, KeyRound,
  Mail, History, ScrollText, Stethoscope, FileSpreadsheet, BellRing, ShieldCheck,
  DatabaseBackup,
} from 'lucide-react';
import { useSession } from '@/core/session';
import { cn } from '@/components/ui';
import { useT } from '@/core/i18n';

/* =============================================================================
 * Settings shell
 * -----------------------------------------------------------------------------
 * Without this, the rules screen was reachable only through the warning box in
 * the sidebar — which vanishes the moment the rules are complete, leaving no
 * route back to the single most important page in the system.
 * ========================================================================== */

/* Exported so the search box can offer these screens.
   ---------------------------------------------------------------------------
   The palette used to carry its own hand-written list of pages, which drifted
   the moment anybody added a screen — somebody typed "backup" into the search
   box and was told there was nothing, with the Backups page sitting two
   sections above it in this very list. One list, read by both. */
export const SECTIONS = [
  { to: 'appearance', labelKey: 'set.appearance', icon: Palette, blurbKey: 'set.appearanceBlurb' },
  { to: 'rules', labelKey: 'set.rules', icon: SlidersHorizontal, blurbKey: 'set.rulesBlurb' },
  { to: 'company', labelKey: 'set.company', icon: Building2, blurbKey: 'set.companyBlurb' },
  { to: 'holidays', labelKey: 'set.holidays', icon: CalendarDays, blurbKey: 'set.holidaysBlurb' },
  /* Directly under the company itself, which is what it is a part of. A
     branch is chosen on an employee's record and printed as a heading on
     every payroll document. */
  { to: 'branches', labelKey: 'set.branches', icon: Building, blurbKey: 'set.branchesBlurb' },
  { to: 'users', labelKey: 'set.users', icon: KeyRound, blurbKey: 'set.usersBlurb' },
  /* Beside the logins rather than at the bottom with the logs. Somebody
     who has just made an account for a payroll officer is one step from
     deciding whether that officer needs a second factor. */
  { to: 'security', labelKey: 'set.security', icon: ShieldCheck, blurbKey: 'set.securityBlurb' },
  /* Next to the import, which is the screen most likely to send somebody
     looking for a copy to go back to. */
  { to: 'backups', labelKey: 'set.backups', icon: DatabaseBackup, blurbKey: 'set.backupsBlurb' },
  { to: 'import', labelKey: 'set.import', icon: FileSpreadsheet, blurbKey: 'set.importBlurb' },
  { to: 'sign-ins', labelKey: 'set.signIns', icon: History, blurbKey: 'set.signInsBlurb' },
  { to: 'activity', labelKey: 'set.activity', icon: ScrollText, blurbKey: 'set.activityBlurb' },
  { to: 'email', labelKey: 'set.email', icon: Mail, blurbKey: 'set.emailBlurb' },
  { to: 'notifications', labelKey: 'set.notifications', icon: BellRing, blurbKey: 'set.notificationsBlurb' },
  { to: 'self-check', labelKey: 'set.selfCheck', icon: Stethoscope, blurbKey: 'set.selfCheckBlurb' },
] as const;

export function SettingsLayout() {
  const t = useT();
  const { policyGaps } = useSession();

  return (
    <div className="max-w-[1100px] mx-auto flex flex-col gap-5">
      <div>
        <h1 className="text-title font-semibold tracking-tight">{t('sl2.settings')}</h1>
        <p className="text-[13px] text-muted mt-0.5">
          {t('sl2.howItBehaves')}
        </p>
      </div>

      <div className="grid lg:grid-cols-[224px_minmax(0,1fr)] gap-5 items-start">
        <nav className="flex lg:flex-col gap-1 scroll-x lg:overflow-visible pb-1">
          {SECTIONS.map(({ to, labelKey, icon: Icon, blurbKey }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-start gap-2.5 px-3 py-2.5 rounded-card shrink-0 lg:w-full',
                  'transition-colors text-start',
                  isActive
                    ? 'bg-brand-subtle text-brand'
                    : 'text-muted hover:bg-surface-2 hover:text-text',
                )
              }
            >
              <Icon size={16} className="shrink-0 mt-0.5" />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold leading-tight whitespace-nowrap lg:whitespace-normal">
                  {t(labelKey)}
                  {/* The count follows you around until it is zero, because a
                      blocked payroll should never be a surprise at month end. */}
                  {to === 'rules' && policyGaps.length > 0 && (
                    <span className="ms-1.5 inline-flex items-center gap-1 text-warn">
                      <AlertTriangle size={11} />
                      <span className="text-[11px] font-bold">{policyGaps.length}</span>
                    </span>
                  )}
                </span>
                <span className="hidden lg:block text-[11px] text-subtle mt-0.5 leading-snug">
                  {t(blurbKey)}
                </span>
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
